import { GridSortItem } from "@mui/x-data-grid";
import {
  Address,
  InsurancePayer,
  InsurancePayerPayerTypeEnum,
  ProviderRoleTypeEnum,
} from "@/services";

export const PAYER_TYPE_LABEL: Record<InsurancePayerPayerTypeEnum, string> = {
  [InsurancePayerPayerTypeEnum.Commercial]: "Commercial",
  [InsurancePayerPayerTypeEnum.Medicare]: "Medicare",
  [InsurancePayerPayerTypeEnum.Medicaid]: "Medicaid",
  [InsurancePayerPayerTypeEnum.MedicaidManagedCare]: "Medicaid Managed Care",
  [InsurancePayerPayerTypeEnum.MedicareAdvantage]: "Medicare Advantage",
  [InsurancePayerPayerTypeEnum.WorkersComp]: "Workers’ Comp",
  [InsurancePayerPayerTypeEnum.AutoPip]: "Auto/PIP",
  [InsurancePayerPayerTypeEnum.TricareVa]: "TRICARE/VA",
  [InsurancePayerPayerTypeEnum.SelfPay]: "Self-Pay",
  [InsurancePayerPayerTypeEnum.Other]: "Other",
};

export const PAYER_TYPE_OPTIONS = Object.values(
  InsurancePayerPayerTypeEnum
).map((value) => ({ value, label: PAYER_TYPE_LABEL[value] }));

export const PAYER_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export const PAYER_STATUS_OPTIONS = [
  { value: PAYER_STATUS.ACTIVE, label: "Active" },
  { value: PAYER_STATUS.INACTIVE, label: "Inactive" },
];

export const PAYER_ACTION = {
  EDIT: "edit",
  ARCHIVE: "archive",
  RESTORE: "restore",
} as const;

export const ADDRESS_LINE_2_FIELD = "line2";

export const PAYER_DEFAULT_COUNTRY = "United States";

export const PAYER_INITIAL_VALUES: PayerFormValues = {
  payerId: "",
  payerName: "",
  payerType: "",
  status: PAYER_STATUS.ACTIVE,
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: PAYER_DEFAULT_COUNTRY,
    zipcode: "",
  },
};

export const PAYER_SEARCH_PLACEHOLDER = "Search by Payer ID or Payer Name";
export const PAYER_NAME_LOOKUP_PAGE_SIZE = 50;

export const PAYER_DEFAULT_SORT: GridSortItem[] = [
  { field: "payerName", sort: "asc" },
];

export const PAYER_SORT_FIELD: Record<string, string> = {
  payerId: "payer_id",
  payerName: "payer_name",
  payerType: "payer_type",
};

export const PAYER_SORTABLE_FIELDS = Object.keys(PAYER_SORT_FIELD);

export const PAYER_FIELD_LENGTH = {
  PAYER_ID: 20,
  PAYER_NAME: 150,
  ADDRESS_LINE: 100,
  CITY: 50,
} as const;

export const PAYER_ID_REGEX = /^(?=.*[A-Za-z0-9])(?=.*[0-9_-])[A-Za-z0-9_-]+$/;
export const PAYER_NAME_LETTER_REGEX = /[A-Za-z]/;
export const PAYER_CITY_REGEX = /^[A-Za-z\s'-]+$/;
export const PAYER_ZIP_REGEX = /^\d{5}(-\d{4})?$/;

export const PAYER_FIELD_LABEL = {
  PAYER_ID: "Payer ID",
  PAYER_NAME: "Payer Name",
  PAYER_TYPE: "Payer Type",
  ADDRESS_LINE_1: "Address Line 1",
  ADDRESS_LINE_2: "Address Line 2",
  CITY: "City",
  STATE: "State",
  ZIP_CODE: "Zip Code",
  COUNTRY: "Country",
} as const;

export const PAYER_TITLE = {
  ADD: "Add Payer",
  EDIT: "Edit Payer",
  PAYER_DETAILS: "Payer Details",
  ADDRESS: "Address",
} as const;

export const PAYER_QUERY_KEY = {
  LIST: "payers-list",
  TOTAL: "payers-total",
  NAME_LOOKUP: "payers-name-lookup",
  ID_LOOKUP: "payers-id-lookup",
  USAGE: "payers-usage",
} as const;

export const PAYER_ADD_ROLES: string[] = [
  ProviderRoleTypeEnum.PracticeAdmin,
  ProviderRoleTypeEnum.BillingAdmin,
];

export const PAYER_ARCHIVE_ROLES: string[] = [
  ProviderRoleTypeEnum.PracticeAdmin,
];

export const PAYER_VIEW_ROLES: string[] = [
  ProviderRoleTypeEnum.PracticeAdmin,
  ProviderRoleTypeEnum.BillingAdmin,
  ProviderRoleTypeEnum.Biller,
  ProviderRoleTypeEnum.FrontDeskStaff,
];

export const PAYER_MESSAGES = {
  DISCARD: "Unsaved changes, discard?",
  ARCHIVE: "Are you sure you want to archive this payer?",
  RESTORE: "Are you sure you want to restore this payer?",
  ARCHIVE_DISABLED: "Mark the payer Inactive before archiving it",
  NAME_EXISTS: "A payer with this name already exists",
  NAME_INVALID: "Payer Name must contain at least one letter",
  PAYER_ID_CHANGE:
    "This will affect future claim submissions and eligibility checks.",
  PAYER_ID_EXISTS: "A payer with this ID already exists",
  ZIP_INVALID: "Enter a valid Zip Code (12345 or 12345-6789)",
  PAYER_ID_INVALID: "Please enter valid payer id",
  CITY_INVALID: "Only letters, spaces, hyphens and apostrophes are allowed",
  ADDRESS_MISSING: "Address missing",
  INACTIVE_SUCCESS: "Payer marked Inactive successfully",
  ACTIVATE_SUCCESS: "Payer activated successfully",
  NO_PAYERS: "No payers added yet",
  NO_MATCHES: "No payers match your search",
  LOAD_FAILED: "Unable to load payers. Please try again.",
} as const;

export const PAYER_CONFIRMATION = {
  [PAYER_ACTION.ARCHIVE]: {
    title: "Archive Payer",
    content: PAYER_MESSAGES.ARCHIVE,
    confirmText: "Archive",
  },
  [PAYER_ACTION.RESTORE]: {
    title: "Restore Payer",
    content: PAYER_MESSAGES.RESTORE,
    confirmText: "Restore",
  },
} as const;

export const PAYER_STATUS_POPUP = {
  INACTIVE_TITLE: "Mark payer Inactive?",
  INACTIVE_CONFIRM: "Inactive",
  ACTIVATE_TITLE: "Activate payer?",
  ACTIVATE_CONFIRM: "Activate",
  NOT_IN_USE:
    "will no longer be available for selection in patient insurance, eligibility checks, claims and invoices.",
  IN_USE_NOTE:
    "They will not be changed, but this payer cannot be selected for new patients, eligibility checks, claims or invoices.",
  ACTIVATE: "will be available for selection again across the platform.",
} as const;

export const PAYER_FILTER_PLACEHOLDER = {
  TYPE: "Payer Type",
  STATUS: "Status",
} as const;

export const PAYER_SHOW_ARCHIVED_LABEL = "Show Archived";
export const PAYER_CLEAR_FILTERS_LABEL = "Clear Filters";

export type PayerStatus = (typeof PAYER_STATUS)[keyof typeof PAYER_STATUS];
export type PayerAction = (typeof PAYER_ACTION)[keyof typeof PAYER_ACTION];
export type PayerType = InsurancePayerPayerTypeEnum;

export type PayerRow = InsurancePayer & { payerId: string };

export interface PayerUsage {
  patientCount: number;
  openClaimCount: number;
}

export interface PayerStatusPrompt {
  payer: PayerRow;
  usage?: PayerUsage;
}

export interface PayerFormValues {
  payerId: string;
  payerName: string;
  payerType: PayerType | "";
  status: PayerStatus;
  address: Address;
}

export interface PayerFilters {
  payerType: PayerType | null;
  status: PayerStatus | null;
}

export interface PayerPermissions {
  canAdd: boolean;
  canEdit: boolean;
  canChangeStatus: boolean;
  canArchive: boolean;
}
